// Per-file `.fit` upload helpers + TanStack mutation hook (U5 of the
// cardio-progress-from-fit plan).
//
// Single-file orchestration only — the component owns the multi-file fan-out
// (via `Promise.allSettled(files.map(...))`) so per-row UI state stays close
// to the rendering layer. This module just centralises:
//   - reading a `File` to an `ArrayBuffer`
//   - POSTing the bytes to `/api/fit/ingest` with the required headers
//   - validating the response against `FitIngestResponseSchema`
//
// The mutation hook is intentionally not configured with `onSuccess` /
// `onError` — the caller wires those up so query invalidation only fires
// once after the *batch* settles (the component awaits all promises).

import { useMutation } from '@tanstack/react-query';

import { apiClient } from './api';
import {
  FitIngestResponseSchema,
  type FitIngestResponse,
} from './types';

/**
 * Read a File into an ArrayBuffer. Wraps the FileReader API in a Promise
 * because the older callback shape doesn't play nicely with async/await.
 */
function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.onload = () => {
      const result = reader.result;
      if (result instanceof ArrayBuffer) resolve(result);
      else reject(new Error('Expected ArrayBuffer from FileReader'));
    };
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Kind hint sent alongside an upload — the sport of the surface the operator
 * clicked UPLOAD .FIT from. The ingest route only uses it to classify a
 * session the parser left at kind='other' (Apple Watch reports racket sports
 * as sport=other); a confident parse always wins, so uploading a run from
 * the padel surface still lands as a run.
 */
export type FitKindHint = 'run' | 'padel';

/**
 * Upload a single `.fit` file to `/api/fit/ingest` and return the parsed
 * `FitIngestResponse`. Throws `ApiError` on a non-2xx response and
 * `ApiNetworkError` when the request couldn't reach the Worker. The caller
 * (the FitUploadButton component) wraps this in `Promise.allSettled` so one
 * file failing doesn't block the others.
 *
 * Headers (matching `apps/protocol-api/src/routes/fit.ts`):
 *   - Content-Type: application/octet-stream
 *   - X-Fit-Source-Filename: encodeURIComponent(file.name) — header values
 *     must be Latin-1, and Apple Watch exports carry names like
 *     "Alexandre’s Apple Watch.fit" (U+2019) that make fetch() throw before
 *     the request leaves the browser. The Worker decodes.
 *   - X-Fit-Kind-Hint: <kindHint> (when provided)
 */
export async function postFitFile(
  file: File,
  kindHint?: FitKindHint,
): Promise<FitIngestResponse> {
  const buf = await readFileAsArrayBuffer(file);
  return apiClient.postBinary<FitIngestResponse>('/api/fit/ingest', buf, {
    schema: FitIngestResponseSchema,
    headers: {
      'X-Fit-Source-Filename': encodeURIComponent(file.name),
      ...(kindHint ? { 'X-Fit-Kind-Hint': kindHint } : {}),
    },
  });
}

/**
 * TanStack mutation wrapper around `postFitFile`. The component instantiates
 * one of these and calls `mutateAsync({ file, kindHint })` for each picked
 * file inside a `Promise.allSettled` — query invalidation happens in the
 * component once the whole batch settles, not inside `onSuccess` here.
 */
export function useFitUploadMutation() {
  return useMutation<FitIngestResponse, Error, { file: File; kindHint?: FitKindHint }>({
    mutationFn: ({ file, kindHint }) => postFitFile(file, kindHint),
  });
}
